import {LitElement, html, css, svg, type TemplateResult} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {morphedRead} from '../../auth/morphed-client.js';
import {userSignal} from '../../auth/auth.store.js';

interface HomeItem {
	titulo: string;
	descripcion: string;
	icon?: string;
}
interface DevLogEntry {
	fecha: string;
	titulo: string;
	cuerpo: string;
	tag: string;
}
interface AdminHome {
	heroTitle?: string;
	heroSubtitle?: string;
	migdrpTitle?: string;
	migdrpIntro?: string;
	migdrpItems?: HomeItem[];
	morphedTitle?: string;
	morphedIntro?: string;
	morphedItems?: HomeItem[];
	clamTitle?: string;
	clamIntro?: string;
	clamItems?: HomeItem[];
	devLog?: DevLogEntry[];
}

const TAG_COLORS: Record<string, {bg: string; fg: string; dot: string}> = {
	feature: {bg: 'rgba(90,157,30,0.14)', fg: '#3f7a12', dot: '#6bbb24'},
	fix: {bg: 'rgba(234,67,53,0.12)', fg: '#c83227', dot: '#ea4335'},
	info: {bg: 'rgba(90,157,30,0.08)', fg: '#5a9d1e', dot: '#84ce29'},
	mejora: {bg: 'rgba(245,158,11,0.14)', fg: '#b4740a', dot: '#f59e0b'},
	seguridad: {bg: 'rgba(126,87,194,0.14)', fg: '#5e3da3', dot: '#7e57c2'},
	default: {bg: 'var(--clam-almost-white, #f0f0f0)', fg: 'var(--clam-text-muted, #6d6e71)', dot: '#aeaeb0'},
};

// Vocabulario de iconos para las tarjetas (icono por tarjeta, elegible desde el admin).
const ICONS: Record<string, TemplateResult> = {
	code: svg`<polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>`,
	cube: svg`<path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>`,
	layers: svg`<path d="m12 2 9 5-9 5-9-5 9-5Z"/><path d="m3 12 9 5 9-5"/><path d="m3 17 9 5 9-5"/>`,
	leaf: svg`<path d="M11 20A7 7 0 0 1 4 13c0-5 5-9 16-9 0 11-4 16-9 16Z"/><path d="M4 20c2-4 6-7 9-8"/>`,
	mail: svg`<rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>`,
	qr: svg`<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 14h3v3h-3z"/><path d="M20 14v3"/><path d="M14 20h3"/><path d="M20 20v1"/>`,
	scan: svg`<path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/><path d="M7 12h10"/>`,
	shield: svg`<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/>`,
	zap: svg`<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>`,
	users: svg`<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>`,
	settings: svg`<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/>`,
	image: svg`<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.5-3.5L9 20"/>`,
	calendar: svg`<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>`,
	check: svg`<path d="M20 6 9 17l-5-5"/>`,
	lock: svg`<rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>`,
	sparkles: svg`<path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3Z"/><path d="M19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9L19 15Z"/>`,
};

function renderIcon(name?: string): unknown {
	const inner = ICONS[name || ''] || ICONS.sparkles;
	return html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="width:1em;height:1em;display:block">${inner}</svg>`;
}

function fmtFecha(fecha: string): string {
	const d = new Date(fecha.length <= 10 ? `${fecha}T00:00:00` : fecha);
	if (Number.isNaN(d.getTime())) return fecha;
	return d.toLocaleDateString('es-MX', {day: '2-digit', month: 'short', year: 'numeric'});
}

type Accent = {brand: string; title: string; intro: string; items: HomeItem[] | undefined; accent: string; soft: string; icon: string};

@customElement('clam-admin-home')
export class ClamAdminHome extends LitElement {
	static styles = css`
		:host {
			display: block;
			font-family: var(--clam-font, 'Inter', system-ui, sans-serif);
			color: var(--clam-text, #333);
			padding: var(--clam-space-6, 24px);
			max-width: 1120px;
			margin: 0 auto;
			box-sizing: border-box;
		}

		/* ===== HERO ===== */
		.hero {
			position: relative;
			overflow: hidden;
			background-color: var(--clam-darkgray, #424244);
			color: var(--clam-white, #fdfdfd);
			border-radius: var(--clam-radius-lg, 12px);
			padding: var(--clam-space-7, 32px) var(--clam-space-6, 24px);
			margin-bottom: var(--clam-space-7, 32px);
			box-shadow: 0 8px 30px rgba(0, 0, 0, 0.22);
		}
		.hero::before {
			content: '';
			position: absolute;
			inset: 0;
			background: radial-gradient(circle at 88% 12%, rgba(107, 187, 36, 0.38), transparent 52%);
			pointer-events: none;
		}
		.hero::after {
			content: '';
			position: absolute;
			inset: 0;
			background-image: radial-gradient(rgba(255, 255, 255, 0.07) 1px, transparent 1px);
			background-size: 18px 18px;
			pointer-events: none;
			opacity: 0.6;
		}
		.hero > * {
			position: relative;
			z-index: 1;
		}
		.hero__inner {
			display: grid;
			grid-template-columns: 1fr auto;
			gap: var(--clam-space-6, 24px);
			align-items: center;
		}
		.hero__date {
			display: inline-block;
			font-size: var(--clam-text-xs, 11px);
			text-transform: uppercase;
			letter-spacing: 1.2px;
			color: var(--clam-white-green, #b6d7a8);
			background: rgba(255, 255, 255, 0.07);
			border: 1px solid rgba(255, 255, 255, 0.1);
			padding: 4px 10px;
			border-radius: 9999px;
			margin: 0 0 var(--clam-space-3, 12px);
		}
		.hero__title {
			margin: 0 0 var(--clam-space-2, 8px);
			font-size: var(--clam-text-2xl, 24px);
			font-weight: 700;
			letter-spacing: -0.01em;
		}
		.hero__subtitle {
			margin: 0;
			font-size: var(--clam-text-base, 14px);
			opacity: 0.82;
			line-height: 1.55;
			max-width: 64ch;
		}
		.hero__user {
			margin-top: var(--clam-space-4, 16px);
			font-size: var(--clam-text-xs, 11px);
			opacity: 0.6;
		}
		.hero__logo {
			width: 84px;
			height: 84px;
			border-radius: 50%;
			background: #313131;
			display: flex;
			align-items: center;
			justify-content: center;
			box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.08), 0 10px 30px rgba(0, 0, 0, 0.45), 0 0 46px rgba(107, 187, 36, 0.3);
		}
		.hero__logo migdrp-logo {
			width: 70px;
			height: 70px;
		}
		.hero__stats {
			display: flex;
			flex-wrap: wrap;
			gap: 10px;
			margin-top: var(--clam-space-5, 20px);
		}
		.stat {
			display: inline-flex;
			align-items: center;
			gap: 8px;
			background: rgba(255, 255, 255, 0.07);
			border: 1px solid rgba(255, 255, 255, 0.1);
			padding: 6px 12px;
			border-radius: 9999px;
			font-size: var(--clam-text-xs, 11px);
			color: rgba(255, 255, 255, 0.82);
		}
		.stat__icon {
			color: var(--clam-white-green, #b6d7a8);
			font-size: 14px;
			display: inline-flex;
		}
		.stat__num {
			font-weight: 700;
			color: #fff;
		}

		/* ===== SECTION ===== */
		.section {
			margin-bottom: var(--clam-space-7, 32px);
		}
		.section__head {
			display: flex;
			align-items: center;
			gap: var(--clam-space-3, 12px);
			margin-bottom: var(--clam-space-2, 8px);
		}
		.section__iconbox {
			width: 40px;
			height: 40px;
			border-radius: 11px;
			background: var(--accent, var(--clam-green, #5a9d1e));
			color: #fff;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 20px;
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
			flex-shrink: 0;
		}
		.section__title {
			margin: 0;
			font-size: var(--clam-text-xl, 20px);
			font-weight: 700;
			color: var(--clam-darkgray, #424244);
			line-height: 1.2;
		}
		.section__brand {
			display: inline-block;
			font-size: 10px;
			font-weight: 700;
			text-transform: uppercase;
			letter-spacing: 0.08em;
			color: var(--accent, var(--clam-green-dark, #3f7a12));
			background: var(--accent-soft, rgba(90, 157, 30, 0.1));
			padding: 2px 8px;
			border-radius: 9999px;
			margin-top: 2px;
		}
		.section__intro {
			margin: var(--clam-space-3, 12px) 0 var(--clam-space-4, 16px);
			color: var(--clam-text-muted, #6d6e71);
			font-size: var(--clam-text-base, 14px);
			line-height: 1.55;
			max-width: 80ch;
		}

		.grid {
			display: grid;
			grid-template-columns: repeat(auto-fill, minmax(min(100%, 230px), 1fr));
			gap: var(--clam-space-4, 16px);
		}
		.card {
			position: relative;
			background: var(--clam-surface, #fff);
			border: 1px solid var(--clam-border, #e2e8f0);
			border-top: 3px solid var(--accent, var(--clam-green, #5a9d1e));
			border-radius: var(--clam-radius-md, 8px);
			padding: var(--clam-space-4, 16px);
			box-shadow: var(--clam-shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.05));
			display: flex;
			flex-direction: column;
			gap: var(--clam-space-2, 8px);
			transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
			overflow: hidden;
		}
		.card::after {
			content: '';
			position: absolute;
			top: 0;
			right: 0;
			width: 70px;
			height: 70px;
			background: radial-gradient(circle at top right, var(--accent-soft, rgba(90, 157, 30, 0.1)), transparent 70%);
			pointer-events: none;
			opacity: 0.8;
		}
		.card:hover {
			transform: translateY(-3px);
			box-shadow: 0 10px 24px rgba(0, 0, 0, 0.1);
		}
		.card__icon {
			width: 38px;
			height: 38px;
			border-radius: 10px;
			background: var(--accent-soft, rgba(90, 157, 30, 0.1));
			color: var(--accent, var(--clam-green, #5a9d1e));
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 19px;
			position: relative;
			z-index: 1;
		}
		.card__title {
			margin: 0;
			font-size: var(--clam-text-base, 14px);
			font-weight: 700;
			color: var(--clam-text, #333);
			position: relative;
			z-index: 1;
		}
		.card__desc {
			margin: 0;
			font-size: var(--clam-text-sm, 13px);
			color: var(--clam-text-muted, #6d6e71);
			line-height: 1.5;
			position: relative;
			z-index: 1;
		}

		/* ===== DEV LOG TIMELINE ===== */
		.devlog {
			position: relative;
			padding-left: 28px;
		}
		.devlog::before {
			content: '';
			position: absolute;
			left: 6px;
			top: 4px;
			bottom: 4px;
			width: 2px;
			background: linear-gradient(var(--clam-light-green, #6bbb24), var(--clam-border, #e2e8f0));
			border-radius: 2px;
		}
		.devlog__entry {
			position: relative;
			background: var(--clam-surface, #fff);
			border: 1px solid var(--clam-border, #e2e8f0);
			border-radius: var(--clam-radius-md, 8px);
			padding: var(--clam-space-3, 12px) var(--clam-space-4, 16px);
			box-shadow: var(--clam-shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.04));
			margin-bottom: var(--clam-space-3, 12px);
		}
		.devlog__entry::before {
			content: '';
			position: absolute;
			left: -26px;
			top: 16px;
			width: 14px;
			height: 14px;
			border-radius: 50%;
			background: var(--clam-surface, #fff);
			border: 3px solid var(--tag-dot, var(--clam-light-green, #6bbb24));
			box-shadow: 0 0 0 3px var(--clam-surface-soft, #f5f6f8);
		}
		.devlog__row {
			display: flex;
			align-items: center;
			gap: var(--clam-space-2, 8px);
			flex-wrap: wrap;
			margin-bottom: 4px;
		}
		.devlog__fecha {
			font-size: var(--clam-text-xs, 11px);
			font-weight: 700;
			color: var(--clam-text-muted, #6d6e71);
			text-transform: uppercase;
			letter-spacing: 0.04em;
		}
		.devlog__tag {
			display: inline-flex;
			align-items: center;
			gap: 5px;
			font-size: 10px;
			font-weight: 700;
			text-transform: uppercase;
			letter-spacing: 0.04em;
			padding: 2px 8px;
			border-radius: 9999px;
		}
		.devlog__tag .dot {
			width: 6px;
			height: 6px;
			border-radius: 50%;
			background: currentColor;
		}
		.devlog__titulo {
			margin: 0;
			font-size: var(--clam-text-base, 14px);
			font-weight: 700;
			color: var(--clam-text, #333);
		}
		.devlog__cuerpo {
			margin: 4px 0 0;
			font-size: var(--clam-text-sm, 13px);
			color: var(--clam-text-muted, #6d6e71);
			line-height: 1.5;
		}

		.empty {
			padding: var(--clam-space-6, 24px);
			text-align: center;
			color: var(--clam-light-gray, #898a8c);
			font-size: var(--clam-text-sm, 13px);
		}

		@media (max-width: 640px) {
			:host {
				padding: var(--clam-space-4, 16px);
			}
			.hero__inner {
				grid-template-columns: 1fr;
			}
			.hero__logo {
				display: none;
			}
		}
	`;

	@property({type: Number}) entityCount = 0;
	@property({type: Number}) toolCount = 0;

	@state() private _data: AdminHome | null = null;
	@state() private _loading = true;

	connectedCallback(): void {
		super.connectedCallback();
		void this._load();
	}

	private async _load(): Promise<void> {
		try {
			const rows = await morphedRead<AdminHome>('Clam Admin Home', {options: {limit: 1}});
			this._data = rows[0] ?? null;
		} catch {
			this._data = null;
		} finally {
			this._loading = false;
			this.requestUpdate();
		}
	}

	private _today(): string {
		return new Date().toLocaleDateString('es-MX', {weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'});
	}

	private _tag(tag: string): {bg: string; fg: string; dot: string} {
		return TAG_COLORS[tag] || TAG_COLORS.default;
	}

	private _renderSection(s: Accent): unknown {
		const items = s.items ?? [];
		return html`
			<section class="section" style="--accent:${s.accent};--accent-soft:${s.soft}">
				<div class="section__head">
					<div class="section__iconbox">${renderIcon(s.icon)}</div>
					<div>
						<h3 class="section__title">${s.title}</h3>
						<span class="section__brand">${s.brand}</span>
					</div>
				</div>
				${s.intro ? html`<p class="section__intro">${s.intro}</p>` : null}
				${items.length
					? html`
						<div class="grid">
							${items.map((it) => html`
								<div class="card">
									<div class="card__icon">${renderIcon(it.icon)}</div>
									<h4 class="card__title">${it.titulo}</h4>
									<p class="card__desc">${it.descripcion}</p>
								</div>
							`)}
						</div>`
					: html`<p class="empty">Sin entradas. Edita "Contenido Inicio" para añadir tarjetas.</p>`}
			</section>
		`;
	}

	private _renderDevLog(entries: DevLogEntry[] = []): unknown {
		const sorted = [...entries].sort((a, b) => (a.fecha < b.fecha ? 1 : a.fecha > b.fecha ? -1 : 0));
		if (!sorted.length) return html`<p class="empty">Sin entradas en el dev log.</p>`;
		return html`
			<div class="devlog">
				${sorted.map((e) => {
					const t = this._tag(e.tag);
					return html`
						<div class="devlog__entry" style="--tag-dot:${t.dot}">
							<div class="devlog__row">
								<span class="devlog__fecha">${fmtFecha(e.fecha)}</span>
								<span class="devlog__tag" style="background:${t.bg};color:${t.fg}"><span class="dot"></span>${e.tag || 'info'}</span>
							</div>
							<h4 class="devlog__titulo">${e.titulo}</h4>
							${e.cuerpo ? html`<p class="devlog__cuerpo">${e.cuerpo}</p>` : null}
						</div>
					`;
				})}
			</div>
		`;
	}

	render(): unknown {
		if (this._loading) {
			return html`<div class="empty">Cargando…</div>`;
		}
		const d = this._data;
		const email = userSignal.value?.email ?? '';
		const devCount = d?.devLog?.length ?? 0;
		return html`
			<header class="hero">
				<div class="hero__inner">
					<div>
						<p class="hero__date">${this._today()}</p>
						<h2 class="hero__title">${d?.heroTitle ?? 'Panel de Administración'}</h2>
						${d?.heroSubtitle ? html`<p class="hero__subtitle">${d.heroSubtitle}</p>` : null}
						<div class="hero__stats">
							<span class="stat"><span class="stat__icon">${renderIcon('layers')}</span><span class="stat__num">${this.entityCount}</span> entidades</span>
							<span class="stat"><span class="stat__icon">${renderIcon('settings')}</span><span class="stat__num">${this.toolCount}</span> herramientas</span>
							<span class="stat"><span class="stat__icon">${renderIcon('sparkles')}</span><span class="stat__num">${devCount}</span> dev log</span>
						</div>
						${email ? html`<p class="hero__user">Sesión: ${email}</p>` : null}
					</div>
					<div class="hero__logo"><migdrp-logo has-text="false"></migdrp-logo></div>
				</div>
			</header>

			${this._renderSection({brand: 'migdrp', title: d?.migdrpTitle || 'Servicios de implementación', intro: d?.migdrpIntro ?? '', items: d?.migdrpItems, accent: '#5a9d1e', soft: 'rgba(90,157,30,0.12)', icon: 'code'})}
			${this._renderSection({brand: 'morphed', title: d?.morphedTitle || 'Tecnología e innovación', intro: d?.morphedIntro ?? '', items: d?.morphedItems, accent: '#5b6cc4', soft: 'rgba(91,108,196,0.13)', icon: 'cube'})}
			${this._renderSection({brand: 'clam', title: d?.clamTitle || 'Lo que puedes hacer en CLAM', intro: d?.clamIntro ?? '', items: d?.clamItems, accent: '#0d9488', soft: 'rgba(13,148,136,0.12)', icon: 'leaf'})}

			<section class="section">
				<div class="section__head">
					<div class="section__iconbox" style="--accent:#424244;background:#424244">${renderIcon('zap')}</div>
					<div>
						<h3 class="section__title">Dev Log</h3>
						<span class="section__brand" style="color:#424244;background:rgba(66,66,68,0.1)">changelog</span>
					</div>
				</div>
				${this._renderDevLog(d?.devLog)}
			</section>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'clam-admin-home': ClamAdminHome;
	}
}
